/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useExtensionOptions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect } from 'react';
import { fetchExtensionOptions } from '../api/extensionsApi';
import { fetchDeviceSipProfiles } from '../../../services/deviceSipProfiles';
import { fetchNumbers as fetchSmsNumbers } from '../../sms/api';
import { fetchFaxDevices } from '../../faxDevices/api/faxDeviceApi';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { ExtensionOptions } from '../types';

const EMPTY_OPTIONS: ExtensionOptions = {
    classOfServices: [],
    dialProfiles: [],
    agentProfiles: [],
    sipProfiles: [],
    smsNumbers: [],
    faxDevices: [],
    dispatchLocations: [],
    aiAgents: [],
    aiTranslatorProfiles: [],
};

interface UseExtensionOptionsResult {
    options: ExtensionOptions;
    isLoadingOptions: boolean;
}

export function useExtensionOptions(
    isEditMode: boolean,
    selectedDomainId?: number,
    onDefaultCosReady?: (cosId: string | number) => void,
    onDefaultDialProfileReady?: (dpId: string | number) => void,
): UseExtensionOptionsResult {
    const [options, setOptions] = useState<ExtensionOptions>(EMPTY_OPTIONS);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadOptions = async () => {
            setIsLoadingOptions(true);
            try {
                const [
                    extensionOptionsResult,
                    deviceProfilesResult,
                    smsNumbersResult,
                    faxDevicesResult,
                    dispatchLocationsResult,
                    aiAgentsResult,
                    aiTranslatorProfilesResult,
                    dialProfilesResult,
                ] = await Promise.allSettled([
                    fetchExtensionOptions(),
                    fetchDeviceSipProfiles(selectedDomainId).catch((e) => { console.warn('SIP Profiles:', e); return []; }),
                    fetchSmsNumbers().catch((e) => { console.warn('SMS Numbers:', e); return []; }),
                    fetchFaxDevices({ page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' }).catch((e) => { console.warn('Fax Devices:', e); return { data: [], pagination: {} }; }),
                    fetch(buildApiUrl('/api/telephony/dispatch-locations?limit=200&sortBy=locationName&sortOrder=asc'), { headers: getApiHeaders() })
                        .then(r => r.json())
                        .then(res => Array.isArray(res?.data) ? res.data : [])
                        .catch(() => []),
                    fetch(buildApiUrl('/api/telephony/ai-agents?limit=100&agentType=chat'), { headers: getApiHeaders() })
                        .then(r => r.json())
                        .then(res => Array.isArray(res?.data) ? res.data : [])
                        .catch(() => []),
                    fetch(buildApiUrl('/api/telephony/ai-agents/translator-profiles/quicklist'), { headers: getApiHeaders() })
                        .then(r => r.json())
                        .then(res => Array.isArray(res?.data) ? res.data : [])
                        .catch(() => []),
                    fetch(buildApiUrl('/api/telephony/dial-profiles?limit=100&sortBy=name&sortOrder=asc'), { headers: getApiHeaders() })
                        .then(r => r.json())
                        .then(res => Array.isArray(res?.data) ? res.data : [])
                        .catch(() => []),
                ]);

                if (!isMounted) return;

                const extensionOptions = extensionOptionsResult.status === 'fulfilled'
                    ? extensionOptionsResult.value
                    : { classOfServices: [], agentProfiles: [], sipProfiles: [], aiTranslatorProfiles: [] };

                const deviceProfilesRaw = deviceProfilesResult.status === 'fulfilled' ? deviceProfilesResult.value : [];
                
                // Filter device SIP profiles to only include internal-based profiles for extensions (exclude external carrier profiles)
                const deviceProfiles = (deviceProfilesRaw || []).filter((p: any) => {
                    const baseName = (p.baseSipProfileName || p.base_sip_profile_name || '').toLowerCase();
                    return baseName === 'internal' || (baseName === '' && p.name !== 'Default External');
                });

                const smsNumbers = smsNumbersResult.status === 'fulfilled' ? smsNumbersResult.value : [];
                const faxDevicesData = faxDevicesResult.status === 'fulfilled'
                    ? (faxDevicesResult.value as { data?: unknown[] }).data ?? []
                    : [];
                const dispatchLocationsData = dispatchLocationsResult.status === 'fulfilled'
                    ? (dispatchLocationsResult.value as Array<{ id: string | number; locationName?: string; location_name?: string; formattedAddress?: string; formatted_address?: string; elin?: string }>) || []
                    : [];
                const aiAgentsData = aiAgentsResult.status === 'fulfilled'
                    ? (aiAgentsResult.value as Array<{ id: number; name?: string; agentName?: string; description?: string; isDefaultChatbot?: boolean }>) || []
                    : [];
                const aiTranslatorProfilesData = aiTranslatorProfilesResult.status === 'fulfilled'
                    ? (aiTranslatorProfilesResult.value as Array<{ id: number; name?: string; description?: string }>) || []
                    : [];
                const dialProfilesData = dialProfilesResult.status === 'fulfilled'
                    ? (dialProfilesResult.value as Array<{ id: number | string; name?: string }>) || []
                    : [];


                // NOTE: ClassOfService IDs are Sqid-encoded strings — do NOT convert to Number
                const normalizedClassOfServices = (extensionOptions.classOfServices || [])
                    .map((cos: { id?: string | number; name?: string; class_name?: string; isMain?: boolean; is_main?: boolean }) => ({
                        id: cos.id,
                        name: cos.name || cos.class_name || String(cos.id || ''),
                        isMain: cos.isMain ?? cos.is_main,
                    }))
                    .filter((cos) => cos.id != null && cos.id !== '' && cos.id !== 0 && cos.name);

                const built: ExtensionOptions = {
                    classOfServices: normalizedClassOfServices,
                    agentProfiles: extensionOptions.agentProfiles || [],
                    sipProfiles: deviceProfiles || [],
                    smsNumbers: (smsNumbers || []).map((n: { id?: number; phoneNumber?: string; phone_number?: string; friendlyName?: string; friendly_name?: string }) => ({
                        id: n.id,
                        phoneNumber: n.phoneNumber || n.phone_number,
                        friendlyName: n.friendlyName || n.friendly_name,
                    })),
                    faxDevices: ((faxDevicesData || []) as Array<{ id: number; name: string; number: string; type?: string; extensionId?: number; extension_id?: number; destinationEmail?: string; destination_email?: string; fromEmail?: string; from_email?: string; enabled?: boolean }>).map((d) => ({
                        id: d.id,
                        name: d.name,
                        number: d.number,
                        type: d.type,
                        extensionId: d.extensionId ?? d.extension_id ?? null,
                        destinationEmail: d.destinationEmail ?? d.destination_email ?? '',
                        fromEmail: d.fromEmail ?? d.from_email ?? '',
                        enabled: d.enabled ?? true,
                    })),
                    dispatchLocations: dispatchLocationsData.map((d) => ({
                        id: Number(d.id),
                        locationName: d.locationName ?? d.location_name ?? '',
                        formattedAddress: d.formattedAddress ?? d.formatted_address ?? '',
                        elin: d.elin ?? '',
                    })),
                    aiAgents: aiAgentsData.map((a) => ({
                        id: a.id,
                        name: a.agentName || a.name || '',
                        description: a.description,
                        isDefaultChatbot: a.isDefaultChatbot,
                    })),
                    aiTranslatorProfiles: aiTranslatorProfilesData.map((a) => ({
                        id: a.id,
                        name: a.name || '',
                        description: a.description,
                    })),
                    dialProfiles: dialProfilesData.map((d: any) => ({
                        id: d.id,
                        name: d.name || String(d.id),
                        isDefault: Boolean(d.isDefault ?? d.is_default),
                    })),
                };

                setOptions(built);

                // Auto-select default COS on new extension
                if (!isEditMode && normalizedClassOfServices.length > 0 && onDefaultCosReady) {
                    const defaultCos = normalizedClassOfServices.length === 1
                        ? normalizedClassOfServices[0]
                        : normalizedClassOfServices.find((cos) => (cos as { isMain?: boolean }).isMain === true);
                    if (defaultCos?.id != null) {
                        onDefaultCosReady(defaultCos.id as string | number);
                    }
                }

                // Auto-select default Dial Profile on new extension
                if (!isEditMode && built.dialProfiles.length > 0 && onDefaultDialProfileReady) {
                    const defaultDp = built.dialProfiles.length === 1
                        ? built.dialProfiles[0]
                        : built.dialProfiles.find((dp: any) => dp.isDefault === true || dp.name === 'Default');
                    if (defaultDp?.id != null) {
                        onDefaultDialProfileReady(defaultDp.id as string | number);
                    }
                }
            } catch (error) {
                console.error('Error loading extension options:', error);
                if (isMounted) setOptions(EMPTY_OPTIONS);
            } finally {
                if (isMounted) setIsLoadingOptions(false);
            }
        };

        loadOptions();
        return () => { isMounted = false; };
    }, [isEditMode, selectedDomainId]);

    return { options, isLoadingOptions };
}
